// ============================================================
// idade.js — a idade a partir da data de nascimento
//
// Calculada sempre, nunca guardada: idade gravada envelhece errado, e a
// planilha do programa já tem "13 anos" escrito ao lado de nomes que hoje
// têm 14.
//
// Existia uma cópia em cada tela que precisava dela, e as cópias
// divergiam: uma delas estava com a expressão quebrada e devolvia null
// para toda data válida, o que fazia a idade aparecer como "—" sempre.
// ============================================================

export const MAIORIDADE = 18;

export function idadeEm(dataISO, hojeISO = null) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(dataISO || ""))) return null;
  const hoje = hojeISO && /^\d{4}-\d{2}-\d{2}$/.test(hojeISO)
    ? new Date(hojeISO + "T12:00:00")
    : new Date();
  const [ano, mes, dia] = dataISO.split("-").map(Number);
  let anos = hoje.getFullYear() - ano;
  const m = hoje.getMonth() + 1, d = hoje.getDate();
  if (m < mes || (m === mes && d < dia)) anos -= 1;
  return anos >= 0 && anos <= 120 ? anos : null;
}

// Maior de idade?
//
// Devolve FALSO quando não há data de nascimento, e isso é a decisão
// importante deste arquivo: o programa é de criança e adolescente, e sem
// saber a idade o certo é tratar como menor. Presumir maioridade pularia
// o consentimento do responsável, que é justamente o que a lei exige —
// enquanto o contrário só pede um documento a mais de quem já podia
// assinar sozinho.
export function ehMaiorDeIdade(dataISO, hojeISO = null) {
  const anos = idadeEm(dataISO, hojeISO);
  return anos !== null && anos >= MAIORIDADE;
}

// A idade com casas decimais, em anos.
//
// `idadeEm` devolve anos inteiros, que é o que a tela mostra. As equações
// de maturação e de composição corporal não aceitam isso: entre 9 e 10
// anos a estimativa de gordura de Deurenberg anda quase um ponto
// percentual, e o pico de velocidade de crescimento é datado em frações
// de ano. Arredondar a idade para baixo jogaria essa diferença inteira
// para dentro do resultado, e o resultado é comparado com o da avaliação
// anterior justamente para enxergar variações desse tamanho.
//
// A parte inteira vem de `idadeEm`, e não de uma divisão por 365,25.
// Dividir o intervalo por 365,25 parece equivalente e não é: no dia em
// que o atleta faz 18 anos a conta dá 17,9986, e o piso disso é 17. A
// tela mostraria 18 e qualquer cálculo que arredondasse para baixo
// trabalharia com 17 — no mesmo dia, para a mesma pessoa, e justamente
// no aniversário que decide quem assina o próprio termo.
//
// Então o inteiro é o mesmo que a tela mostra, e o que se soma é só a
// fração do ano de vida em curso, medida entre o último aniversário e o
// próximo. Nascido em 29 de fevereiro cai no 1º de março nos anos não
// bissextos, que é como o resto do sistema já trata a data.
export function idadeDecimalEm(dataISO, refISO = null) {
  const anos = idadeEm(dataISO, refISO);
  if (anos === null) return null;
  if (refISO && !/^\d{4}-\d{2}-\d{2}$/.test(refISO)) return null;

  const [ano, mes, dia] = dataISO.split("-").map(Number);
  const ref = refISO ? new Date(refISO + "T12:00:00") : new Date();
  if (Number.isNaN(ref.getTime())) return null;

  const aniversario = n => new Date(ano + n, mes - 1, dia, 12, 0, 0);
  const ultimo = aniversario(anos);
  const proximo = aniversario(anos + 1);
  return anos + (ref - ultimo) / (proximo - ultimo);
}
